import ECSEditor from 'components/editor/ECSEditor';
import ECSEditorSheet from 'components/editor/ECSEditorSheet';
import InteractionControls from 'components/viewer/toolbars/InteractionControls';
import ZoomControls from 'components/viewer/toolbars/ZoomControls';
import Immutable from 'immutable';
import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { UpdatePunch } from 'records/interactions/UpdatePunch';
import { ViewPunchList } from 'records/interactions/ViewPunchList';
import { Sheet } from 'records/Sheet';
import { SheetVersion } from 'records/SheetVersion';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import ManagePostRFIInteraction from './ManagePostRFIInteraction';
import ManageUpdatePunchInteraction from './ManageUpdatePunchInteraction';
import ViewerFooter from './ViewerFooter';
import ViewerHeader from './ViewerHeader';

import PlacePunchMessageOverlay from './PlacePunchMessageOverlay';

interface Params {
  sheetId: string;
  projectId: string;
}

interface Props extends WithAppStoreProps, RouteComponentProps<Params> {}

interface State {
  dataLoaded: boolean;
}

class ViewerPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      dataLoaded: false,
    };
  }

  async componentDidMount(): Promise<void> {
    const {
      appState,
      actions,
      match: { params },
    } = this.props;

    await actions.getRFIs();
    this.setRFIQuery();

    // TODO: Handle case of sheetId not present in returned sheets.
    if (!appState.sheets.has(params.sheetId)) {
      await actions.getSheets();
    }

    const queryParams = new URLSearchParams(this.props.location.search);
    const focusedMarkup = queryParams.get('markup');
    if (focusedMarkup) {
      await actions.setFocusedMarkup(focusedMarkup);
    }

    await actions.setSelectedSheet(params.sheetId);
    await actions.getProjectGroups();
    // Refetching punches just to make sure
    await actions.getPunches(appState.selectedProject);

    if (focusedMarkup && focusedMarkup !== '') {
      const { punches, sheets } = appState;
      const punch = punches.find((p) => p.markupids.contains(focusedMarkup));

      // Open UpdatePunch Overlay
      if (punch) {
        const sheet = sheets.get(params.sheetId, new Sheet());
        const sheetversion = sheet.versions.last<SheetVersion>();

        const interaction = new UpdatePunch({
          viewerOverlay: true,
          punch,
          fileid: sheetversion.pageid,
        });

        actions.clearInteractions();
        actions.createInteraction(interaction);
      }
    }

    if (!appState.sheetQuery.initialized) {
      actions.initSheetQuery();
    }

    this.setState({ dataLoaded: true });
  }

  componentDidUpdate(prevProps: Props): void {
    const {
      actions,
      appState,
      match: { params },
    } = this.props;
    const { dataLoaded } = this.state;

    if (!dataLoaded) {
      return;
    }

    if (params.sheetId !== prevProps.match.params.sheetId) {
      actions.setSelectedSheet(params.sheetId);

      if (appState.interactions.some((i) => i.type === 'ViewPunchList')) {
        const sheet = appState.getIn(['sheets', params.sheetId]) as Sheet;
        actions.clearInteractions();
        actions.createInteraction(
          new ViewPunchList({
            sheet,
            resetPunchQuery: true,
          })
        );
      }

      this.setRFIQuery();
    }
  }

  componentWillUnmount(): void {
    const { actions } = this.props;

    // TODO: Add this back in before checking it in!!
    this.props.actions.clearInteractions();

    // remove state
    actions.setSelectedSheet('');

    // Reset punch and rfi search when leaving viewer.
    actions.initPunchQuery();
    actions.initRFIQuery();
  }

  setRFIQuery = () => {
    const {
      actions,
      appState,
      match: { params },
    } = this.props;
    const sheet = appState.getIn(['sheets', params.sheetId]) as Sheet;
    const sheetVersionsPageIds = sheet.versions.map(({ pageid }) => pageid);
    const sheetVersionsPageIdsArray = Array.from(sheetVersionsPageIds);
    const nextRFIQuery = appState.rfiQuery.set(
      'pages',
      Immutable.Set(sheetVersionsPageIdsArray)
    );
    actions.setRFIQuery(nextRFIQuery);
  };

  render(): React.ReactNode {
    const { dataLoaded } = this.state;
    const { sheetId } = this.props.match.params;
    const {
      appState: { sheets, interactions },
      history,
    } = this.props;
    const sheet = sheets.get(sheetId, new Sheet());

    if (sheet.versions.size === 0) {
      return null;
    }

    const hasPostRFIInteraction =
      interactions.filter((i) => i.type === 'PostRFI').size > 0;

    const hasPlacePunchInteraction =
      interactions.filter((i) => i.type === 'PlacePunch').size > 0;

    const editorContainerId = 'sheet-viewer-container';

    return (
      <div
        id="viewer-page"
        style={{ height: '90%', touchAction: 'none' }}
        className="vh100 column justify-between"
      >
        {dataLoaded ? (
          <>
            <ViewerHeader sheet={sheet} history={history} />
            <ECSEditor containerId={editorContainerId} isModal={false}>
              <ECSEditorSheet
                key={sheet.sheetid}
                sheet={sheet}
                withMarkups
                isInteractive
              />
              {hasPlacePunchInteraction ? <PlacePunchMessageOverlay /> : null}
              {!hasPostRFIInteraction && !hasPlacePunchInteraction ? (
                <>
                  <InteractionControls sheetid={sheet.sheetid} />
                  <ZoomControls />
                </>
              ) : null}
              <ManagePostRFIInteraction />
              <ManageUpdatePunchInteraction />
            </ECSEditor>
            <ViewerFooter sheet={sheet} history={history} />
          </>
        ) : null}
      </div>
    );
  }
}

export default withAppStore<Props>(ViewerPage);
